import type { ComputedRef, Ref } from 'vue'
import type { RegularFilter } from '../models/store/filters/filter'
import type { RegularFilterSelectedValues } from '../models/store/filters/filter-selected-values'
import { computed, unref } from 'vue'
import { FilterTypeEnum } from '../enums/filter-type.enum'
import { FiltersUtils } from '../utils/filters.utils'
import { useEpb } from './use-epb'
import { useFilters } from './use-filters'
import { useInstance } from './use-instance'

interface ExplainFiltersComposable {
  filters: ComputedRef<Record<string, RegularFilter>>
  showExplainFilters: ComputedRef<boolean>
  showExplainFiltersAside: ComputedRef<boolean>
  areFiltersValid: ComputedRef<boolean>
}

interface ExplainFiltersComposableOptions {
  areAllFiltersMandatory: Ref<boolean> | boolean
}

export function useExplainFilters({
  areAllFiltersMandatory,
}: ExplainFiltersComposableOptions): ExplainFiltersComposable {
  const { $epb } = useEpb()
  const { instanceConfig } = useInstance()
  const { availableFilters } = useFilters()
  const allowMultipleValuesInSearchFilters = instanceConfig.value.allowMultipleValuesInSearchFilters

  const filters = computed(() => {
    const regularFilters = FiltersUtils.filterByType(
      availableFilters.value,
      FilterTypeEnum.REGULAR,
    ) as Record<string, RegularFilter>
    return FiltersUtils.updateFilters(regularFilters, (filter: RegularFilter) => {
      let result = FiltersUtils.updateFilterConfig(filter, {
        mandatory: unref(areAllFiltersMandatory) && !filter.config.notMandatoryInExplain,
        multiselectDisabled: !allowMultipleValuesInSearchFilters,
      })
      result = FiltersUtils.updateRegularFilterSelectedValues(
        result,
        getDefaultSelectedValues(result),
      )

      return result
    })
  })

  function getDefaultSelectedValues(filter: RegularFilter): RegularFilterSelectedValues {
    if (filter.selectedValues.length > 1 && filter.config.multiselectDisabled) {
      return filter.selectedValues.slice(0, 1)
    }

    if (filter.selectedValues.length > 0) {
      return filter.selectedValues
    }

    return FiltersUtils.getDefaultSelectedValues(filter, false)
  }

  const someFiltersHaveMultipleValues = computed(() =>
    Object.values(filters.value).some((filter: RegularFilter) => filter.config.values.length > 1),
  )

  const showExplainFilters = computed(
    () => $epb.showExplainFilters && someFiltersHaveMultipleValues.value,
  )

  const showExplainFiltersAside = computed(
    () => $epb.showExplainFiltersAside && someFiltersHaveMultipleValues.value,
  )

  const mandatoryFilters = computed(() =>
    Object.values(filters.value).filter((filter: RegularFilter) => filter.config.mandatory),
  )

  const areMandatoryFiltersValid = computed(() =>
    mandatoryFilters.value.every(isMandatoryFilterValid),
  )

  function isMandatoryFilterValid(filter: RegularFilter): boolean {
    return (
      !!filter.selectedValues.length &&
      filter.config.values.some(({ id }) => id === filter.selectedValues[0])
    )
  }

  const areFiltersEnabled = computed(
    () => showExplainFilters.value || showExplainFiltersAside.value,
  )

  // Filters are valid if:
  // - They are disabled via FFs, or
  // - They are enabled and either:
  //   - They are not mandatory in the current section, or
  //   - They are mandatory in the current section and they are all valid.
  const areFiltersValid = computed(() => {
    return (
      !areFiltersEnabled.value ||
      (areFiltersEnabled.value &&
        (!unref(areAllFiltersMandatory) ||
          (unref(areAllFiltersMandatory) && areMandatoryFiltersValid.value)))
    )
  })

  return {
    areFiltersValid,
    filters,
    showExplainFilters,
    showExplainFiltersAside,
  }
}
